import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { IIframeAppInput } from "libraries/component/iframe-app-embedder/iframe-app-interfaces";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ResourceService } from "libraries/service/resource/resource.service";
import { InstanceService } from 'libraries/service/instance/instance.service';
import { LanguageService } from 'libraries/service/language/language.service';
import { Mock4allSingletonService } from "libraries/mock4all/mock4all-singleton.service";
import { IWenji } from "libraries/interfaces/IWenji";
import { PriceSwitcherService } from "libraries/component/tab-navigation/price-switcher.service";
import { Subscription } from "rxjs";
import { EXRateType } from "libraries/interfaces/ITotal";

declare var wenji: IWenji;

export interface IActionOutput {
    name: string;
    type?: string;
    [key: string]: any;
}

export interface IFlutterMessage {
    name: string;
    xtoken?: string;
    apiUrl?: string;
    language?: string;
    additionalData?: object;
    mockId?: string;
    target?: string;
    priceType?: EXRateType;

    [key: string]: any;
}

export interface IIframeEvent {
    data: IActionOutput;
    newHeight?: number;
    origin: string;
    iframeName: string;
}

@Component({
    selector: 'iframe-app-embedder',
    template: `
        <iframe #myIframe
                [name]="name"
                [attr.src]="safeUrl"
                [class.full-height]="isFullHeight"
                [class.is-hidden]="isHidden"
                [style.height]="newHeight"></iframe>
    `,
    styles: [`
        iframe {
            border: 0;
            width: 100%;
        }

        iframe.full-height {
            height: 100%;
        }

        iframe.is-hidden {
            display: none;
        }

        :host {
            display: block;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IframeAppEmbedderComponent implements OnInit, OnChanges, OnDestroy {

    @Input()
    public iframeSrc: string;

    @Input()
    public isHidden: boolean;

    @Input()
    public additionalData: IIframeAppInput;

    @Input()
    public isFullHeight: boolean;

    @Input()
    public isDynamicHeight: boolean;

    @Input()
    public iframeName: string;

    @Output()
    public readonly onIframeEvent: EventEmitter<IActionOutput> = new EventEmitter<IActionOutput>();

    @ViewChild('myIframe')
    public myIframe: ElementRef;

    public safeUrl: SafeResourceUrl;

    public newHeight: string;
    public isLoaded: boolean;
    public name: string;

    public subscription: Subscription = new Subscription();

    constructor(
        private readonly domSanitizer: DomSanitizer,
        private readonly resourceService: ResourceService,
        private readonly instanceService: InstanceService,
        private readonly languageService: LanguageService,
        private readonly mock4allSingletonService: Mock4allSingletonService,
        private readonly priceSwitcherService: PriceSwitcherService,
    ) {
    }

    public ngOnInit(): void {
        this.name = `iframe${Math.random().toString()}`;
        this.safeUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(this.iframeSrc);
        this.subscription.add(
            this.priceSwitcherService.selectPriceEvent
                .subscribe((priceType: EXRateType): void => {
                    let message: IFlutterMessage = {
                        name: "changePriceType",
                        priceType
                    };

                    console.info(this.iframeName, {message});
                    this.myIframe.nativeElement.contentWindow.postMessage(
                        message,
                        "*",
                    );
                })
        );
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes?.additionalData?.currentValue && !changes?.additionalData?.firstChange && this.isLoaded) {
            setTimeout(
                (): void => {
                    this.postInitMessageToIframe();
                },
                10
            );
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    @HostListener('window:message', ['$event'])
    public onMessage(event: IIframeEvent): void {
        if (!this.iframeName || (this.iframeName === event.data.iframeName)) {
            if (event.data.loaded) {
                this.isLoaded = true;
                setTimeout(
                    (): void => {
                        this.postInitMessageToIframe();
                    },
                    10
                );
            } else if (event.data.newHeight && this.isDynamicHeight) {
                this.newHeight = `${event.data.newHeight}px`;
            } else {
                this.emit(event.data);
            }
        }
    }

    public postInitMessageToIframe(): void {

        //define api for endpoints used in flutter apps
        const apiUrl: string = `${this.instanceService.fullUrl}/api/`;

        //get request parameters
        const mockId: string = this.mock4allSingletonService.mockId;
        const target: string = wenji.ui.target;

        //add color from admin
        const additionalData: object = {
            ...this.additionalData,
            style: wenji.ui.flutterStyle,
            priceType: this.priceSwitcherService.selectedPrice || EXRateType.cost,
        };

        //define message
        let message: IFlutterMessage = {
            name: "init",
            xtoken: this.resourceService.getTokenPublic(),
            apiUrl,
            language: this.languageService.userLanguage,
            additionalData,
            target,
            priceType: this.priceSwitcherService.selectedPrice || EXRateType.cost,
        };

        if (this.priceSwitcherService.hasPriceSwitcher) {
            message = {
                ...message,
                priceType: this.priceSwitcherService.selectedPrice,
            }
        }

        // add mockIf if exist
        if (mockId) {
            message.mockId = mockId;
        }

        console.info(this.iframeName, {message});

        this.myIframe.nativeElement.contentWindow.postMessage(
            message,
            "*",
        );
    }

    private emit(data: IActionOutput): void {
        if (!data.type || !data.type.includes('webpack')) {
            this.onIframeEvent.emit(data);
        }
    }
}
